import React from 'react'
import {
    Box,
    Code,
    Divider,
    Heading,
    Link,
    ListItem,
    OrderedList,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    UnorderedList,
} from '@chakra-ui/react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

/**
 * Renders the support-bot's assistant messages as styled markdown.
 *
 * Why this exists separate from the bubble component:
 *   The bot returns rich markdown (headers, tables, fenced code blocks,
 *   blockquotes, inline code for IDs/paths). Rendering it as `pre-wrap`
 *   text produces a wall of literal asterisks and pipes, which is unusable
 *   for actual support investigations. We map every block- and inline-
 *   level element to a Chakra primitive so the rendered output inherits
 *   the dashboard's typography + dark-mode story automatically.
 *
 * Why not user messages too:
 *   User messages are plain text the operator typed; rendering them as
 *   markdown would silently transform `*` and `_` characters in IDs /
 *   queries / log lines into bold/italic. The chat-bubble component opts
 *   in per-role.
 *
 * Why react-markdown + remark-gfm:
 *   - react-markdown is the de-facto React-native markdown renderer; the
 *     `components` prop lets us map element-by-element to Chakra without
 *     touching `dangerouslySetInnerHTML`.
 *   - remark-gfm adds GitHub Flavored Markdown: tables (heavily used by the
 *     bot for diff-style "Surface | Behavior" matrices), strikethrough,
 *     autolinks, task lists. Not optional given current bot output shape.
 *   - Both are ESM-only at v9 / v4 respectively. Gatsby 5 handles this
 *     fine; if you ever hit "ERR_REQUIRE_ESM" during a build, downgrade
 *     to react-markdown@8 + remark-gfm@3 (CJS-compatible) — same API.
 *
 * Security note:
 *   react-markdown does NOT render raw HTML embedded in markdown by
 *   default (`html` is disabled at the parser level). That's the right
 *   default for a SuperAdmin tool too — defense-in-depth in case a
 *   prompt injection ever convinces the bot to emit `<script>` tags.
 *   Don't add `rehype-raw` to this pipeline without revisiting the
 *   threat model.
 */
interface MarkdownMessageProps {
    content: string
}

export default function MarkdownMessage({ content }: MarkdownMessageProps) {
    return (
        <Box
            // Tighten the natural Chakra primitives a bit so chat bubbles
            // don't have desktop-doc-page spacing. Most rules below set
            // their own margins; this catches stragglers (e.g. final
            // child margin collapsing).
            sx={{
                '& > *:first-of-type': { mt: 0 },
                '& > *:last-of-type': { mb: 0 },
            }}
        >
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={CHAT_MARKDOWN_COMPONENTS}
            >
                {content}
            </ReactMarkdown>
        </Box>
    )
}

/**
 * Maps every markdown element react-markdown can produce to a Chakra
 * component. Defined at module scope (not inline in the component) so
 * react-markdown doesn't tear down + re-create the element renderers on
 * every parent render — important since assistant messages are sometimes
 * thousands of nodes.
 *
 * Typed as `Components` from react-markdown so a future library upgrade
 * that adds/removes element mappings is caught at compile time.
 */
const CHAT_MARKDOWN_COMPONENTS: Components = {
    // Block-level prose ----------------------------------------------------

    p: ({ children }) => (
        <Text fontSize="sm" lineHeight="tall" my={2}>
            {children}
        </Text>
    ),

    // Headings: chat bubbles aren't documents, so de-emphasize the cascade.
    // h1 ≈ doc title; in chat we never want that loud, so cap at md/sm sizes.
    h1: ({ children }) => (
        <Heading as="h3" fontSize="md" fontWeight="bold" mt={4} mb={2}>
            {children}
        </Heading>
    ),
    h2: ({ children }) => (
        <Heading as="h4" fontSize="md" fontWeight="bold" mt={4} mb={2}>
            {children}
        </Heading>
    ),
    h3: ({ children }) => (
        <Heading as="h5" fontSize="sm" fontWeight="bold" mt={3} mb={1}>
            {children}
        </Heading>
    ),
    h4: ({ children }) => (
        <Heading as="h6" fontSize="sm" fontWeight="semibold" mt={3} mb={1}>
            {children}
        </Heading>
    ),
    h5: ({ children }) => (
        <Heading as="h6" fontSize="xs" fontWeight="semibold" mt={2} mb={1}>
            {children}
        </Heading>
    ),
    h6: ({ children }) => (
        <Heading as="h6" fontSize="xs" fontWeight="semibold" mt={2} mb={1}>
            {children}
        </Heading>
    ),

    // Lists ----------------------------------------------------------------

    ul: ({ children }) => (
        <UnorderedList fontSize="sm" lineHeight="tall" my={2} pl={4}>
            {children}
        </UnorderedList>
    ),
    ol: ({ children }) => (
        <OrderedList fontSize="sm" lineHeight="tall" my={2} pl={4}>
            {children}
        </OrderedList>
    ),
    li: ({ children }) => <ListItem mb={1}>{children}</ListItem>,

    // Blockquote — used heavily for "suggested reply" sections in bot output.
    blockquote: ({ children }) => (
        <Box
            as="blockquote"
            borderLeftWidth={3}
            borderLeftColor="brand.300"
            bg="white"
            px={3}
            py={2}
            my={2}
            sx={{
                // Inner paragraphs already have my={2}; collapse so the
                // blockquote feels tight.
                '& > p': { my: 1 },
            }}
        >
            {children}
        </Box>
    ),

    // Inline + block code --------------------------------------------------

    /**
     * react-markdown collapses both inline code (`x`) and fenced blocks
     * (```x```) onto the same `code` component. We discriminate by checking
     * whether the parent is a `pre` (block) vs not (inline). The component
     * receives a `className` like "language-typescript" on fenced blocks
     * and undefined on inline.
     */
    code: ({ className, children, ...props }) => {
        const isInline =
            !className || !/language-/.test(className)
        if (isInline) {
            return (
                <Code
                    fontSize="xs"
                    px={1}
                    py={0}
                    borderRadius="sm"
                    bg="gray.200"
                    color="gray.900"
                    {...props}
                >
                    {children}
                </Code>
            )
        }
        // Block code: just render the raw text. The `pre` wrapper below
        // handles the box, scrolling, and syntax-light styling. We don't
        // ship a syntax highlighter because (a) chat code blocks are short,
        // (b) bundle weight isn't worth it for a SuperAdmin tool, (c) the
        // bot's code blocks are typically TS snippets that read fine
        // unhighlighted.
        return <Box as="code" display="block">{children}</Box>
    },

    pre: ({ children }) => (
        <Box
            as="pre"
            bg="gray.900"
            color="gray.100"
            fontSize="xs"
            fontFamily="mono"
            p={3}
            my={2}
            borderRadius="md"
            overflowX="auto"
            sx={{
                // Inner code element should NOT add its own background.
                '& code': {
                    bg: 'transparent',
                    color: 'inherit',
                    p: 0,
                    fontSize: 'inherit',
                },
            }}
        >
            {children}
        </Box>
    ),

    // Tables (GFM) ---------------------------------------------------------
    //
    // Wrap in a horizontally-scrollable Box so wide tables don't blow out
    // the chat bubble width on narrow conversations.
    table: ({ children }) => (
        <Box overflowX="auto" my={3}>
            <Table
                size="sm"
                variant="simple"
                fontSize="xs"
                sx={{
                    // Borders all-around — Chakra's `simple` variant only
                    // bottom-borders rows by default; for chat tables a
                    // gridded look reads better.
                    'th, td': {
                        borderWidth: 1,
                        borderColor: 'gray.300',
                    },
                }}
            >
                {children}
            </Table>
        </Box>
    ),
    thead: ({ children }) => <Thead bg="gray.200">{children}</Thead>,
    tbody: ({ children }) => <Tbody>{children}</Tbody>,
    tr: ({ children }) => <Tr>{children}</Tr>,
    th: ({ children }) => (
        <Th fontSize="xs" textTransform="none" py={2} px={3}>
            {children}
        </Th>
    ),
    td: ({ children }) => (
        <Td fontSize="xs" py={2} px={3}>
            {children}
        </Td>
    ),

    // Misc -----------------------------------------------------------------

    hr: () => <Divider my={3} borderColor="gray.300" />,

    a: ({ href, children }) => (
        <Link
            href={href}
            isExternal
            color="brand.600"
            textDecoration="underline"
        >
            {children}
        </Link>
    ),

    // Strong/em map to default <strong>/<em> via inheritance — Chakra's
    // CSSReset gives them sensible bold/italic styles already, no need
    // to override.
}
